import { gsap } from 'gsap';
import { ScrollToPlugin } from 'gsap/ScrollToPlugin';

gsap.registerPlugin(ScrollToPlugin);

/**
 * Smooth scrolling for same page anchors, site wide.
 *
 * GSAP rather than CSS scroll-behavior because spinning-logo.js calls
 * ScrollTrigger.normalizeScroll(), which takes scrolling over from the
 * browser. Native smooth scrolling fights it; GSAP's scroller does not.
 */
export const smoothAnchors = (offset = 0) => {
    const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');

    const target = (hash) => {
        if (!hash || hash === '#' || hash.length < 2) return null;
        try {
            return document.querySelector(hash);
        } catch (e) {
            return null; // an href like "#" followed by something that is not a valid selector
        }
    };

    // Longer scrolls take a little longer, but never enough to feel slow.
    // ease-in-out because the page is moving on screen rather than entering it.
    const goTo = (el, animate = true) => {
        const distance = Math.abs(el.getBoundingClientRect().top);
        const duration = animate && !reduced.matches
            ? Math.min(0.8, Math.max(0.4, distance / 2400))
            : 0;

        gsap.to(window, {
            duration,
            ease: 'power3.inOut',
            scrollTo: { y: el, offsetY: offset, autoKill: true },
            onComplete: () => {
                // send the caret and any screen reader to where the page just went
                if (!el.hasAttribute('tabindex')) {
                    el.setAttribute('tabindex', '-1');
                }
                el.focus({ preventScroll: true });
            },
        });
    };

    document.addEventListener('click', (event) => {
        const link = event.target.closest('a[href*="#"]');
        if (!link || link.hasAttribute('download') || link.target === '_blank') return;

        // only same page links
        if (link.pathname !== window.location.pathname || link.host !== window.location.host) return;

        const el = target(link.hash);
        if (!el) return;

        event.preventDefault();
        goTo(el);

        if (window.history && window.history.pushState) {
            window.history.pushState(null, '', link.hash);
        }
    });

    // a hash already in the address bar on load
    if (window.location.hash) {
        const el = target(window.location.hash);
        if (el) {
            window.requestAnimationFrame(() => goTo(el, false));
        }
    }
};
